// src/components/listing/ListingCard.js
// Carte d'annonce (utilisée dans les listes) - NE PAS MODIFIER

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PriceBadge from './PriceBadge';
import DistanceBadge from './DistanceBadge';
import { useListings } from '../../hooks/useListings';

const ListingCard = ({ listing }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isFavorite, addToFavorites, removeFromFavorites } = useListings();

  if (!listing) return null;

  const {
    id,
    slug,
    title,
    quartier,
    city,
    price,
    priceType,
    category,
    images,
    bedrooms,
    bathrooms,
    area,
    distance,
  } = listing;

  const mainImage =
    images && images.length > 0 ? images[0] : '/images/placeholder.png';

  const favorite = isFavorite(id);

  const handleCardClick = () => {
    navigate(`/listing/${slug}`);
  };

  const handleFavoriteClick = (e) => {
    e.stopPropagation();
    if (favorite) {
      removeFromFavorites(id);
    } else {
      addToFavorites(id);
    }
  };

  const styles = {
    card: {
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: '#162033',
      borderRadius: '12px',
      overflow: 'hidden',
      cursor: 'pointer',
      transition: 'transform 200ms ease-in-out',
      border: '1px solid rgba(255, 255, 255, 0.1)',
      marginBottom: '16px',
    },
    imageContainer: {
      position: 'relative',
      width: '100%',
      height: '200px',
      backgroundColor: '#111827',
      overflow: 'hidden',
    },
    image: {
      width: '100%',
      height: '100%',
      objectFit: 'cover',
    },
    distanceBadgeContainer: {
      position: 'absolute',
      top: '12px',
      left: '12px',
    },
    favoriteButton: {
      position: 'absolute',
      top: '12px',
      right: '12px',
      width: '36px',
      height: '36px',
      borderRadius: '50%',
      backgroundColor: 'rgba(0, 0, 0, 0.6)',
      backdropFilter: 'blur(4px)',
      border: 'none',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      fontSize: '18px',
      transition: 'transform 200ms',
    },
    content: {
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
      padding: '12px 16px 16px',
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      gap: '8px',
    },
    title: {
      fontSize: '16px',
      fontWeight: '600',
      color: '#FFFFFF',
      lineHeight: '1.3',
      flex: 1,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      display: '-webkit-box',
      WebkitLineClamp: 2,
      WebkitBoxOrient: 'vertical',
    },
    location: {
      fontSize: '13px',
      color: '#A0AEC0',
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
    },
    details: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      fontSize: '12px',
      color: '#A0AEC0',
    },
    detailItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
    },
    footer: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginTop: '4px',
    },
  };

  return (
    <div
      style={styles.card}
      onClick={handleCardClick}
      onMouseEnter={(e) => (e.currentTarget.style.transform = 'scale(1.01)')}
      onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
    >
      <div style={styles.imageContainer}>
        <img src={mainImage} alt={title} style={styles.image} />
        {distance !== undefined && distance !== null && (
          <div style={styles.distanceBadgeContainer}>
            <DistanceBadge distance={distance} />
          </div>
        )}
        <button
          style={styles.favoriteButton}
          onClick={handleFavoriteClick}
        >
          {favorite ? '❤️' : '🤍'}
        </button>
      </div>

      <div style={styles.content}>
        <div style={styles.header}>
          <div style={styles.title}>{title}</div>
        </div>

        <div style={styles.location}>
          📍 {quartier}{city ? `, ${city}` : ''}
        </div>

        {(bedrooms || bathrooms || area) && (
          <div style={styles.details}>
            {bedrooms && (
              <div style={styles.detailItem}>
                🛏️ {bedrooms} {bedrooms > 1 ? t('listing.bedrooms') : t('listing.bedroom')}
              </div>
            )}
            {bathrooms && (
              <div style={styles.detailItem}>
                🚿 {bathrooms} {bathrooms > 1 ? t('listing.bathrooms') : t('listing.bathroom')}
              </div>
            )}
            {area && (
              <div style={styles.detailItem}>
                📐 {area} m²
              </div>
            )}
          </div>
        )}

        <div style={styles.footer}>
          <PriceBadge price={price} priceType={priceType} size="medium" />
        </div>
      </div>
    </div>
  );
};

export default ListingCard;